import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, IconButton, Pagination, Typography } from "@mui/material";

import { UserRole } from "../../../../types/types";

import { ArrowBackButtonIcon } from "../../assets/icons/go-hire-icons";
import { ServiceCategory, ServiceTypeEnum } from "../../enum/service.enum";
import { useTopServiceProviders } from "../../api/queries/useTopServiceProviders";
import { useFilterState } from "../../../../shared/FilterAutocomplete/useFilterState";
import { ServiceProviderCard, ServiceProviderCardLoader } from "../../components/ServiceProviderCard";

import Filters from "./Filters";
import PersonaFilter from "./PersonaFilter";

type ProviderFilters = {
  countryIds: string;
  type: ServiceTypeEnum;
  categories: ServiceCategory;
  rating: number;
  availabilityOption: "online" | "offline";
};

const TrendingProvidersPage = () => {
  const { t, i18n } = useTranslation("goService", { keyPrefix: "trendingProvidersPage" });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 20;
  const role = searchParams.get("role")?.toUpperCase() as UserRole | undefined;

  const { filters } = useFilterState<ProviderFilters>({
    fields: {
      countryIds: {
        type: "string",
      },
      type: {
        type: "string",
      },
      categories: {
        type: "string",
      },
      rating: {
        type: "number",
      },
      availabilityOption: {
        type: "string",
      },
    },
  });

  const { data: topServiceProviders, isLoading: isTopServiceProvidersLoading } = useTopServiceProviders({
    limit,
    offset: (page - 1) * limit,
    ...filters,
    role,
  });

  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", newPage.toString());
    setSearchParams(newParams);
  };

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackButtonIcon />
        </IconButton>
        {t("title")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
        <PersonaFilter isDataLoading={isTopServiceProvidersLoading} />
        <Filters isDataLoading={isTopServiceProvidersLoading} />
      </Box>
      <Box sx={styles.providersContainer}>
        {isTopServiceProvidersLoading &&
          Array.from({ length: 10 }).map((_, idx) => <ServiceProviderCardLoader key={idx} />)}
        {!isTopServiceProvidersLoading &&
          topServiceProviders?.items.map((user) => <ServiceProviderCard key={user.id} user={user} />)}
      </Box>

      {topServiceProviders?.meta && topServiceProviders.meta.totalPages > 1 && (
        <Box sx={styles.paginationContainer}>
          <Pagination
            count={topServiceProviders.meta.totalPages}
            page={page}
            onChange={handlePageChange}
            sx={styles.pagination}
          />
        </Box>
      )}
    </Box>
  );
};

export default TrendingProvidersPage;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
    padding: "20px",
  },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 800,
    lineHeight: "normal",
  },
  providersContainer: {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(2, 1fr)",
      sm: "repeat(3, 1fr)",
      md: "repeat(4, 1fr)",
    },
    // display: "flex",
    // flexWrap: "wrap",
    gap: "16px",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "24px",
  },
  pagination: {
    marginBottom: "45px",
    direction: "ltr !important",
    "& .MuiButtonBase-root": {
      borderRadius: "8px !important",
    },
    "& .MuiPagination-ul": {
      justifyContent: "center",
    },
  },
};
